import { ExperienceAnchor } from '@dadohr/shared/domainTypes/experience';
import { css } from '@emotion/css';
import { useCallback } from 'react';
import {
  ItemSorter,
  RowsRenderer,
  ROW_HEIGHTS
} from '../../../../../components/GroupableList';
import { useSort } from '../../../../../hooks/useSort';
import { useTheme } from '../../../../../themes';
import { AnchorField } from '../../../components/Anchors';
import {
  D,
  ManualEnrollTableColumn,
  ManualEnrollTableItemSorters,
  OtherProps
} from './columns';

const ROW_TO_KEY = (d: D) => d.participant.id;

export const ManualEnrollTable = ({
  ds,
  columns,
  sorters,
  selected,
  onChangeSelected,
  variant = 'contained',
  headProps = {},
  templateAnchor,
  anchorFields,
  otherProps
}: {
  ds: D[];
  selected: Set<string>;
  onChangeSelected: (nextSelected: Set<string>) => void;
  columns: ManualEnrollTableColumn[];
  sorters: ManualEnrollTableItemSorters;
  variant?: 'contained' | 'plain';
  headProps?: {
    sticky?: boolean;
    offset?: number;
  };
  templateAnchor: ExperienceAnchor;
  anchorFields: AnchorField[];
  otherProps: OtherProps;
}) => {
  const theme = useTheme();
  const { sortBy, direction, setSort } = useSort<ItemSorter<D, OtherProps>>(
    sorters.name,
    sorters.name.items.dir
  );

  const onRowClick = useCallback(
    (d: D) => {
      if (!d.isSelectable) {
        return;
      }

      const nextSelected = new Set([...selected]);
      if (nextSelected.has(d.participant.id)) {
        nextSelected.delete(d.participant.id);
      } else {
        nextSelected.add(d.participant.id);
      }
      onChangeSelected(nextSelected);
    },
    [selected, onChangeSelected]
  );

  const rowToClassName = useCallback(
    (d: D) => {
      if (!d.isSelectable) {
        return css({
          background: theme.custom.colors.gray.w100,
          cursor: 'default !important'
        });
      }
      return '';
    },

    [theme]
  );

  return (
    <RowsRenderer
      variant={variant}
      columns={columns}
      rows={ds}
      rowToKey={ROW_TO_KEY}
      onRowClick={onRowClick}
      sorter={sortBy}
      sortDirection={direction}
      renderHead={true}
      headProps={headProps}
      onHeadClick={(c, dir) => {
        const sorter = sorters[c.key];
        if (!sorter) {
          return;
        }
        setSort(sorter, dir || sorter.items.dir);
      }}
      chunkSize={30}
      rowToClassName={rowToClassName}
      rootMargin="300px"
      rowHeight={ROW_HEIGHTS.dense}
      otherProps={otherProps}
    />
  );
};
